/**
 * React Query mutation hook for uploading PDFs to ShowNotes blocks
 */

import { useMutation } from '@tanstack/react-query'
import httpClient, { pluckData } from 'src/utilities/http-client'
import axios from 'axios'

interface UploadPDFRequest {
  blockID: string
  file: File
}

async function getPresignedUrlForUpload(
  blockID: string,
  basePath: string,
): Promise<string> {
  let response = await httpClient.put(`/api/${basePath}/blocks/${blockID}/pdf`)
  return pluckData(response)
}

async function uploadPDFToS3(presignedUrl: string, file: File): Promise<void> {
  await axios.put(presignedUrl, file, {
    headers: {
      'Cache-Control': 'max-age=2592000',
      'content-type': 'application/pdf',
    },
  })
}

/**
 * Upload PDF file to S3
 * Backend automatically extracts S3 key and updates block with metadata
 */
async function uploadBlockPDF(
  { blockID, file }: UploadPDFRequest,
  basePath: string,
): Promise<void> {
  let presignedUrl = await getPresignedUrlForUpload(blockID, basePath)

  // Validate presigned URL to prevent SSRF attacks
  if (
    typeof presignedUrl !== 'string' ||
    !presignedUrl.startsWith('https://')
  ) {
    // eslint-disable-next-line no-console
    console.error('Invalid presigned URL received')
    return
  }

  let url = new URL(presignedUrl)
  if (!url.hostname.endsWith('.amazonaws.com')) {
    // eslint-disable-next-line no-console
    console.error('Presigned URL must be from S3')
    return
  }
  if (
    !url.searchParams.has('X-Amz-Signature') &&
    !url.searchParams.has('Signature')
  ) {
    // eslint-disable-next-line no-console
    console.error('URL must be a presigned S3 URL')
    return
  }

  await uploadPDFToS3(presignedUrl, file)
}

/**
 * Hook to upload a PDF for a show notes block
 * Backend automatically extracts the S3 key and updates the block
 */
export function useUploadBlockPDF(basePath: string) {
  return useMutation({
    mutationFn: (request: UploadPDFRequest) =>
      uploadBlockPDF(request, basePath),
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to upload block PDF:', error)
    },
  })
}
